import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from '@/types/customer';
import { formatCurrency } from '@/utils/format';
import { invoiceApi } from '@/api/endpoints/invoices';
import { InvoiceResponse } from '@/types/invoice';

interface CustomerFinancialInfoProps {
  customer: Customer;
  selectedInvoiceIds?: string[];
  onInvoiceSelectionChange?: (invoiceIds: string[], invoiceCodes: string[]) => void;
  receivedAmount?: number;
  onReceivedAmountChange?: (amount: number) => void;
  inputMode?: 'amount' | 'selection' | null;
}

const CustomerFinancialInfo: React.FC<CustomerFinancialInfoProps> = ({ 
  customer,
  selectedInvoiceIds = [],
  onInvoiceSelectionChange,
  receivedAmount = 0,
  onReceivedAmountChange,
  inputMode = null
}) => {
  const { t } = useTranslation();
  const [incompleteInvoices, setIncompleteInvoices] = useState<InvoiceResponse[]>([]);
  const [allInvoices, setAllInvoices] = useState<InvoiceResponse[]>([]); // Store all invoices for calculation
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0); // Total count from server
  const [searchInput, setSearchInput] = useState(''); // Search input state
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pageSize = 5;

  // Fetch incomplete invoices for the customer
  useEffect(() => {
    const fetchIncompleteInvoices = async () => {
      if (!customer?.code) return;
      
      const wasFocused = document.activeElement === searchInputRef.current;
      
      setLoadingInvoices(true);
      try {
        const response = await invoiceApi.getInvoices(currentPage, pageSize, {
          customer_id: customer.code,
          search: searchInput.trim(), // Use search input directly
          status: 'waiting_payment' // Only get invoices waiting for payment
        });
        // Use all invoices from response since we already filtered by status
        setIncompleteInvoices(response.data);
        
        // Update allInvoices - merge with existing data from other pages
        setAllInvoices(prev => {
          // Remove invoices from current page
          const filtered = prev.filter(inv => !response.data.some((i: InvoiceResponse) => i.id === inv.id));
          // Add new invoices from current page
          return [...filtered, ...response.data];
        });
        
        // Use total from server response for pagination
        setTotalPages(Math.ceil(response.total / pageSize));
        setTotalInvoices(response.total);
        
        // Restore focus if it was focused before
        if (wasFocused && searchInputRef.current) {
          searchInputRef.current.focus();
        }
      } catch (error) {
        console.error('Error fetching incomplete invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    };
    
    fetchIncompleteInvoices();
  }, [customer?.code, currentPage, searchInput]);

  // Calculate which invoices can be paid (fully or partially) with the received amount
  const calculatePayableInvoices = () => {
    let remainingAmount = receivedAmount;
    const fullyPayableIds: string[] = [];
    let partiallyPayableId: string | null = null;
    let partialAmount = 0;
    
    for (const invoice of incompleteInvoices) {
      const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
      
      if (remainingAmount >= remaining) {
        fullyPayableIds.push(invoice.id);
        remainingAmount -= remaining;
      } else if (remainingAmount > 0) {
        // Partial payment for this invoice
        partiallyPayableId = invoice.id;
        partialAmount = remainingAmount;
        break;
      } else {
        break;
      }
    }
    
    return { fullyPayableIds, partiallyPayableId, partialAmount };
  };

  const { fullyPayableIds, partiallyPayableId, partialAmount } = calculatePayableInvoices();

  // Helper function to get invoice codes from IDs
  const getInvoiceCodesFromIds = (ids: string[]): string[] => {
    return ids.map(id => {
      const invoice = incompleteInvoices.find(inv => inv.id === id);
      return invoice?.code || '';
    }).filter(code => code !== '');
  };

  // Auto-select affected invoices when received amount changes (only in amount mode)
  useEffect(() => {
    // Only run in amount mode
    if (inputMode !== 'amount' || !onInvoiceSelectionChange) return;
    
    if (receivedAmount === 0) {
      // Clear selection when amount is 0
      onInvoiceSelectionChange([], []);
      return;
    }
    
    const affectedIds = [...fullyPayableIds];
    if (partiallyPayableId) {
      affectedIds.push(partiallyPayableId);
    }
    
    // Only update if selection actually changed
    const currentSelection = JSON.stringify([...selectedInvoiceIds].sort());
    const newSelection = JSON.stringify([...affectedIds].sort());
    
    if (currentSelection !== newSelection) {
      const codes = getInvoiceCodesFromIds(affectedIds);
      onInvoiceSelectionChange(affectedIds, codes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedAmount, incompleteInvoices.length]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((invoiceId: string, checked: boolean) => {
    if (!onInvoiceSelectionChange) return;
    
    let newIds: string[];
    if (checked) {
      newIds = [...selectedInvoiceIds, invoiceId];
    } else {
      newIds = selectedInvoiceIds.filter(id => id !== invoiceId);
    }
    
    const codes = getInvoiceCodesFromIds(newIds);
    onInvoiceSelectionChange(newIds, codes);
  }, [selectedInvoiceIds, onInvoiceSelectionChange, incompleteInvoices]);

  // Calculate total amount from selected invoices (use allInvoices, not just current page)
  const calculateTotalFromSelection = () => {
    let total = 0;
    for (const invoice of allInvoices) {
      if (selectedInvoiceIds.includes(invoice.id)) {
        const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
        total += remaining;
      }
    }
    return total;
  };

  // Auto-calculate received amount when invoices are manually selected (only in selection mode)
  useEffect(() => {
    // Only run in selection mode
    if (inputMode !== 'selection' || !onReceivedAmountChange) return;
    
    // Calculate total from selected invoices
    const totalFromSelection = calculateTotalFromSelection();
    
    // Update amount if it's different
    if (totalFromSelection !== receivedAmount) {
      onReceivedAmountChange(totalFromSelection);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceIds, allInvoices.length, inputMode]);

  // Disable checkboxes only in amount mode
  const isCheckboxDisabled = inputMode === 'amount';

  // Memoize search input to prevent losing focus
  const searchInputElement = useMemo(() => (
    <input
      ref={searchInputRef}
      type="text"
      placeholder={t('receiptVoucher.form.searchNote')}
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
    />
  ), [searchInput, t]);

  return (
    <div className="space-y-4">
      {/* Compact Customer Info Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900">{customer.name}</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {customer.code} • {customer.phone}
            </p>
          </div>
        </div>
        
        {/* Financial Summary - Compact Grid */}
        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-500">{t('receiptVoucher.form.customerDebt')}</p>
            <p className={`text-sm font-semibold mt-0.5 ${
              (customer.totalDebt || 0) > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {formatCurrency(customer.totalDebt || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('receiptVoucher.form.totalSales')}</p>
            <p className="text-sm font-semibold text-blue-600 mt-0.5">
              {formatCurrency(customer.totalSell || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('receiptVoucher.form.paymentStatus')}</p>
            <div className="flex items-center mt-0.5">
              <div className={`w-2 h-2 rounded-full mr-1.5 ${
                (customer.debit || 0) > 0 ? 'bg-red-500' : 'bg-green-500'
              }`}></div>
              <span className={`text-xs font-medium ${
                (customer.debit || 0) > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {(customer.debit || 0) > 0 
                  ? t('receiptVoucher.form.hasDebt') 
                  : t('receiptVoucher.form.noDebt')
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Two Tables Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Table 1: Available Invoices */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('receiptVoucher.form.incompleteInvoices')} ({totalInvoices})
            </h4>
            {/* Search input - memoized to prevent losing focus */}
            {searchInputElement}
          </div>
          
          {loadingInvoices ? (
            <p className="text-sm text-gray-500 text-center py-4">{t('common.loading')}...</p>
          ) : incompleteInvoices.length > 0 ? (
            <>
              <div className="space-y-2">
            {incompleteInvoices.map((invoice) => {
              const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
              const isChecked = selectedInvoiceIds.includes(invoice.id);
              
              // Only show highlight in amount mode
              const isFullyPayable = inputMode === 'amount' && fullyPayableIds.includes(invoice.id);
              const isPartiallyPayable = inputMode === 'amount' && partiallyPayableId === invoice.id;
              
              // Calculate shortage for partially payable invoice
              const shortage = isPartiallyPayable ? remaining - partialAmount : 0;
              
              return (
                <div
                  key={invoice.id}
                  onClick={() => !isCheckboxDisabled && handleCheckboxChange(invoice.id, !isChecked)}
                  className={`flex items-center gap-2 p-2 rounded border hover:bg-gray-100 cursor-pointer ${
                    isFullyPayable
                      ? 'bg-green-50 border-green-300'
                      : isPartiallyPayable
                      ? 'bg-yellow-50 border-yellow-300'
                      : 'bg-gray-50 border-gray-100'
                  } ${isCheckboxDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation(); // Prevent double-triggering from row click
                      handleCheckboxChange(invoice.id, e.target.checked);
                    }}
                    disabled={isCheckboxDisabled}
                    className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                      isCheckboxDisabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  
                  {/* Invoice Info - Compact */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {invoice.code}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {invoice.order_id}
                    </p>
                    {invoice.note && invoice.note.trim() !== '' && (
                      <p className="text-xs text-gray-600 italic truncate">
                        {invoice.note}
                      </p>
                    )}
                  </div>
                  
                  {/* Amount - Compact */}
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${
                      isFullyPayable 
                        ? 'text-green-700' 
                        : isPartiallyPayable 
                        ? 'text-yellow-700' 
                        : 'text-gray-900'
                    }`}>
                      {formatCurrency(remaining)}
                    </p>
                    {isFullyPayable && (
                      <p className="text-xs text-green-600 font-medium">
                        ✓ {t('receiptVoucher.form.fullPayment')}
                      </p>
                    )}
                    {isPartiallyPayable && shortage > 0 && (
                      <p className="text-xs text-orange-600 font-medium">
                        {t('receiptVoucher.form.shortage')}: {formatCurrency(shortage)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              
              {/* Page numbers */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          )}
          </>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            {t('receiptVoucher.form.noIncompleteInvoices')}
          </p>
        )}
        </div>

        {/* Table 2: Selected Invoices */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            {t('receiptVoucher.form.selectedInvoices')} ({selectedInvoiceIds.length})
          </h4>
          
          {selectedInvoiceIds.length > 0 ? (
            <div className="space-y-2">
              {allInvoices
                .filter(inv => selectedInvoiceIds.includes(inv.id))
                .map((invoice) => {
                  const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
                  
                  // Check if this invoice is fully or partially payable
                  const isFullyPayable = inputMode === 'amount' && fullyPayableIds.includes(invoice.id);
                  const isPartiallyPayable = inputMode === 'amount' && partiallyPayableId === invoice.id;
                  const shortage = isPartiallyPayable ? remaining - partialAmount : 0;
                  
                  return (
                    <div
                      key={invoice.id}
                      onClick={() => inputMode !== 'amount' && handleCheckboxChange(invoice.id, false)}
                      className={`flex items-center gap-3 p-2 rounded border ${
                        isFullyPayable
                          ? 'bg-green-50 border-green-300'
                          : isPartiallyPayable
                          ? 'bg-yellow-50 border-yellow-300'
                          : 'bg-blue-50 border-blue-200'
                      } ${inputMode !== 'amount' ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                    >
                      {/* Remove button - only show in selection mode */}
                      {inputMode !== 'amount' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent double-triggering from row click
                            handleCheckboxChange(invoice.id, false);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title={t('tooltips.common.remove')}
                        >
                          ✕
                        </button>
                      )}
                      
                      {/* Invoice Info - Compact */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {invoice.code}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {invoice.order_id}
                        </p>
                        {invoice.note && invoice.note.trim() !== '' && (
                          <p className="text-xs text-gray-600 italic truncate">
                            {invoice.note}
                          </p>
                        )}
                      </div>
                      
                      {/* Amount Info - Compact */}
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${
                          isFullyPayable 
                            ? 'text-green-700' 
                            : isPartiallyPayable 
                            ? 'text-yellow-700' 
                            : 'text-gray-900'
                        }`}>
                          {formatCurrency(remaining)}
                        </p>
                        {isFullyPayable && (
                          <p className="text-xs text-green-600 font-medium">
                            ✓ {t('receiptVoucher.form.fullPayment')}
                          </p>
                        )}
                        {isPartiallyPayable && shortage > 0 && (
                          <p className="text-xs text-orange-600 font-medium">
                            {t('receiptVoucher.form.shortage')}: {formatCurrency(shortage)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              {t('receiptVoucher.form.noSelectedInvoices')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerFinancialInfo;
